// Substance Profile — Chrona Review feature (A2F-1363/A2F-1361, dev-gated).
//
// One table per substance (enumerated by product). Renders the backend contract
// (GET /substance-profile) — the engine decides, the UI renders. Implements the Substance
// screen of Review Interaction Model.dc.html: leads with the Molecular Weight cell, honest
// about the rest.
//
// Render states reuse the Study Profile model + add `computed` (a calc-vs-measured
// arithmetic finding, A2F-1363): LHS (calculated) · vs measured · RHS (measured).
//   quiet | blank | authority | equivalent | computed | unextractable(+pendingOwner=pending)
//
// Strength/fill mass (→ A2F-1374) and Composition (→ A2F-1372) render as pending rows.
// Dismiss/restore use the SHARED reconciliation finding store (one finding, one dismissal
// across surfaces); Part 11 audited.
//
// NOTE: the render helpers (EvidenceItem / chips / verdict pills / FeatureRow) are duplicated
// from StudyProfilePage for now to keep this surface self-contained; extracting a shared
// profile-render module is a tracked tidy-up.

import { useCallback, useEffect, useState } from 'react';
import {
  TriangleAlert, ArrowRight, Check, Clock, EyeOff, ChevronDown, RefreshCw,
  RotateCcw, Shield, Lock, LoaderCircle, FlaskConical, FileQuestion, X,
} from 'lucide-react';

import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { api } from '@/lib/api';
import { useScanTask } from '@/lib/useScanTask';
import { ScanFooter } from '@/components/shared/ScanFooter';
import type {
  SubstanceProfileResponse, SubstanceProfileEntity, SubstanceProfileRow, StudyProfileEvidence,
} from '@/types/api';

const MIN_RATIONALE = 10;

function verdictPill(verdict: string): string {
  const base = 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap';
  if (/source|authoritative|measured/i.test(verdict)) return `${base} bg-white border border-brand-charcoal text-brand-charcoal`;
  if (/drift|discrepan|calculated/i.test(verdict)) return `${base} bg-amber-50 border border-amber-200 text-amber-700`;
  if (/unreadable|unextractable/i.test(verdict)) return `${base} bg-white border border-dashed border-slate-300 text-slate-600`;
  return `${base} bg-white border border-slate-200 text-slate-500`;
}

function verdictLabel(verdict: string): string {
  const map: Record<string, string> = {
    authoritative_source: 'authoritative source', authoritative: 'authoritative source',
    drifted: 'drifted', consistent: 'consistent', unattributed: 'unattributed',
    blank: 'not stated', unextractable: 'could not read',
  };
  return map[verdict] ?? verdict.replace(/_/g, ' ');
}

// One per-document evidence record (no eCTD module badge — the doc name carries its section).
function EvidenceItem({ ev }: { ev: StudyProfileEvidence }) {
  return (
    <div className="py-2.5 border-t border-slate-100">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-[13px] font-semibold text-brand-charcoal truncate">{ev.docName}</span>
          {ev.versionLabel && (
            <span className="font-mono text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-px">
              {ev.versionLabel}
            </span>
          )}
        </div>
        <span className={verdictPill(ev.verdict)}>{verdictLabel(ev.verdict)}</span>
      </div>
      {ev.state === 'stated' && ev.verbatim && (
        <div className="mt-1.5 pl-3 border-l-2 border-slate-200 text-[13px] leading-relaxed text-slate-600">
          <span className="text-slate-400">&ldquo;</span>
          {ev.verbatim.before}
          <span className="rounded px-1 font-mono font-semibold bg-orange-50 text-amber-800">{ev.verbatim.match}</span>
          {ev.verbatim.after}
          <span className="text-slate-400">&rdquo;</span>
        </div>
      )}
      {ev.state === 'unextractable' && (
        <div className="mt-1.5 flex items-center gap-2 px-2.5 py-2 rounded-md border border-dashed border-slate-300 bg-slate-50 text-[12.5px] font-medium text-slate-600">
          <EyeOff className="w-3.5 h-3.5 shrink-0" />
          {ev.unextractableReason || 'Chrona could not read this document.'}
        </div>
      )}
    </div>
  );
}

function QuietChip({ value, count, qualifier }: { value: string; count: number; qualifier?: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[13px] font-medium bg-slate-50 border border-slate-200 text-slate-700 rounded-md px-2.5 py-1">
      {value}
      {qualifier && <span className="font-sans text-[10px] text-slate-400 not-italic">({qualifier})</span>}
      {count > 1 && <span className="text-slate-400 font-semibold text-[11px]">×{count}</span>}
    </span>
  );
}

function DriftChip({ value, tone }: { value: string; tone: 'drift' | 'source' }) {
  const cls = tone === 'source'
    ? 'bg-white border border-brand-charcoal text-brand-charcoal'
    : 'bg-amber-50 border border-amber-200 text-amber-700';
  return (
    <span className={`inline-flex items-center font-mono text-[13px] font-semibold rounded-md px-2.5 py-1 whitespace-nowrap ${cls}`}>
      {value}
    </span>
  );
}

const FLAGGED_RENDERS = new Set(['authority', 'equivalent', 'computed']);

function RowBody({ row }: { row: SubstanceProfileRow }) {
  // PENDING — render="unextractable" + pendingOwner (strength → A2F-1374, composition → A2F-1372).
  if (row.render === 'unextractable' && row.pendingOwner) {
    return (
      <>
        <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-dashed border-slate-300 bg-slate-50 text-[10.5px] font-bold uppercase tracking-wider text-slate-500">
            <Clock className="w-3 h-3" /> pending
          </span>
          <span className="text-[12.5px] text-slate-500">{row.label} isn&apos;t wired yet — coming with {row.pendingOwner}.</span>
        </div>
        <div className="flex-none flex items-center gap-3 pt-px">
          <span className="text-[11px] font-semibold text-slate-400">pending · {row.pendingOwner}</span>
        </div>
      </>
    );
  }

  let chips: React.ReactNode = null;
  let tail: React.ReactNode = null;

  if (row.render === 'blank') {
    chips = <span className="text-lg text-slate-300 leading-none">—</span>;
    tail = <span className="text-[11px] font-semibold text-slate-400">not stated by any source</span>;
  } else if (row.render === 'quiet') {
    chips = (
      <div className="flex items-center gap-2 flex-wrap">
        {row.variations.map((v, i) => (
          <QuietChip key={i} value={`${v.value}${v.unit ? ' ' + v.unit : ''}`} count={v.docCount} qualifier={v.qualifier} />
        ))}
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <span className="w-[7px] h-[7px] rounded-full bg-emerald-500" /> consistent
      </span>
    );
  } else if (row.render === 'equivalent') {
    // Multiple molecular forms (free base / salt / monomer) — distinct, qualified, NOT a drift.
    chips = (
      <div className="flex items-center gap-2 flex-wrap">
        {row.variations.map((v, i) => (
          <QuietChip key={i} value={`${v.value}${v.unit ? ' ' + v.unit : ''}`} count={v.docCount} qualifier={v.qualifier} />
        ))}
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <span className="w-[7px] h-[7px] rounded-full bg-emerald-500" /> consistent across forms
      </span>
    );
  } else if (row.render === 'authority') {
    const auth = row.authority;
    chips = (
      <div className="flex items-center gap-2.5 flex-wrap">
        {(auth?.suspectValues ?? []).filter(Boolean).map((v, i) => <DriftChip key={i} value={String(v)} tone="drift" />)}
        <ArrowRight className="w-4 h-4 text-amber-600" />
        {auth?.authoritativeValue && <DriftChip value={auth.authoritativeValue} tone="source" />}
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">source</span>
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
        <TriangleAlert className="w-3 h-3" /> likely drift
      </span>
    );
  } else if (row.render === 'computed') {
    // calc-vs-measured: authoritativeValue = measured (stated), suspectValues[0] = calculated.
    const auth = row.authority;
    chips = (
      <div className="flex items-center gap-2 flex-wrap font-mono text-[13px]">
        <span className="font-sans text-[12px] text-slate-500">calculated</span>
        <span className="font-semibold text-brand-charcoal">{auth?.suspectValues?.[0] ?? '—'}</span>
        <span className="font-sans text-[11px] text-slate-400">vs measured</span>
        <span className="font-semibold text-brand-charcoal">{auth?.authoritativeValue ?? '—'}</span>
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
        <TriangleAlert className="w-3 h-3" /> doesn&apos;t reconcile
      </span>
    );
  } else {
    chips = <span className="text-[12.5px] text-slate-400">Not available</span>;
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <EyeOff className="w-3 h-3" /> unavailable
      </span>
    );
  }

  return (
    <>
      <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">{chips}</div>
      <div className="flex-none flex items-center gap-3 pt-px">{tail}</div>
    </>
  );
}

function FeatureRow({
  row, expanded, onToggle, canDismiss, onDismiss, onRestore,
}: {
  row: SubstanceProfileRow;
  expanded: boolean;
  onToggle: () => void;
  canDismiss: boolean;
  onDismiss: (key: string) => void;
  onRestore: (key: string) => void;
}) {
  const flagged = FLAGGED_RENDERS.has(row.render) && row.verdict === 'drifted' && !row.dismissed;
  return (
    <div>
      <div
        className={`feat-row flex items-start gap-4 py-3.5 cursor-pointer border-b border-slate-100 hover:bg-[#FCFBFA] transition-colors ${
          flagged ? 'border-l-2 border-l-brand-orange pl-[18px] pr-5' : 'px-5'
        }`}
        onClick={onToggle}
      >
        <div className="w-[138px] flex-none text-[13px] font-semibold text-brand-charcoal pt-0.5">
          {row.label}
          {row.dismissed && <span className="block text-[10px] font-medium text-slate-400 mt-0.5">dismissed</span>}
        </div>
        <RowBody row={row} />
        <ChevronDown className={`w-[15px] h-[15px] text-slate-400 transition-transform flex-none mt-0.5 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-6 pb-5 pl-[158px] bg-[#FCFBFA] border-b border-slate-100">
          {row.authority?.reason && (
            <p className="text-[12px] text-slate-500 mt-3 leading-relaxed">{row.authority.reason}</p>
          )}
          {row.evidence.length === 0 && (
            <p className="text-[12px] text-slate-400 italic mt-3">No per-document evidence for this feature.</p>
          )}
          {row.evidence.map((ev, i) => <EvidenceItem key={i} ev={ev} />)}

          {row.findingKey && (
            <div className="mt-3.5 flex items-center gap-2">
              {row.dismissed ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRestore(row.findingKey!); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
              ) : canDismiss ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDismiss(row.findingKey!); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white"
                >
                  <Shield className="w-3.5 h-3.5" /> Dismiss with rationale
                </button>
              ) : null}
              {row.dismissed && row.dismissReason && (
                <span className="text-[11px] text-slate-400 italic">&ldquo;{row.dismissReason}&rdquo;</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubstanceCard({
  entity, expanded, toggleRow, canDismiss, onDismiss, onRestore,
}: {
  entity: SubstanceProfileEntity;
  expanded: Record<string, boolean>;
  toggleRow: (id: string) => void;
  canDismiss: boolean;
  onDismiss: (key: string) => void;
  onRestore: (key: string) => void;
}) {
  const [showConsistent, setShowConsistent] = useState(true);
  const isConsistent = (r: SubstanceProfileRow) =>
    (r.render === 'quiet' || r.render === 'blank' || r.render === 'equivalent') && !r.dismissed;
  const consistentCount = entity.rows.filter(isConsistent).length;
  const visibleRows = entity.rows.filter((r) => showConsistent || !isConsistent(r));
  const attention = entity.status === 'attention';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-none ${attention ? 'bg-amber-500' : 'bg-emerald-500'}`} />
          <div className="min-w-0">
            <div className="font-bold text-[15px] text-brand-charcoal truncate">{entity.title}</div>
            {entity.subtitle && <div className="text-[11px] text-slate-500 mt-px">{entity.subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3.5 flex-none">
          <span className={`text-[11px] font-semibold ${attention ? 'text-amber-700' : 'text-emerald-700'}`}>
            {attention ? 'Needs attention' : 'All consistent'}
          </span>
          {consistentCount > 0 && (
            <button
              type="button"
              onClick={() => setShowConsistent((s) => !s)}
              className="text-[11px] font-semibold text-brand-orange bg-transparent border-0 py-1"
            >
              {showConsistent ? 'Hide consistent' : `Show ${consistentCount} consistent`}
            </button>
          )}
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <div className="px-5 py-5 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-none" />
          <span className="text-[13px] text-slate-500">All features consistent across sources.</span>
        </div>
      ) : visibleRows.map((row) => {
        const id = `${entity.substanceId}.${row.feature}`;
        return (
          <FeatureRow
            key={id}
            row={row}
            expanded={!!expanded[id]}
            onToggle={() => toggleRow(id)}
            canDismiss={canDismiss}
            onDismiss={onDismiss}
            onRestore={onRestore}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SubstanceProfilePage() {
  const { activeProject } = useProject();
  const { canRunScans, canDismissFindings } = usePermissions();
  const projectId = activeProject?.id;

  const [result, setResult] = useState<SubstanceProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [modalKey, setModalKey] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');

  const { taskState, startTask, cancel: cancelScan, result: scanResult } =
    useScanTask<SubstanceProfileResponse>('substance_profile');
  const scanning = taskState.isScanning;

  const loadCached = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const r = await api.substanceProfile.get(projectId);
      setResult(r);
      setScanned(r != null);
      setError(null);
    } catch (e) {
      const transient = e instanceof Error && /failed to fetch|networkerror|load failed/i.test(e.message);
      if (!transient) setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const runScan = useCallback(async () => {
    if (!projectId) return;
    setError(null);
    try {
      const { taskId } = await api.substanceProfile.scan(projectId, undefined, undefined, true);
      startTask(taskId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    }
  }, [projectId, startTask]);

  useEffect(() => {
    void loadCached();
    if (!projectId) return;
    api.tasks
      .findRunning(projectId, 'substance_profile_scan')
      .then((task) => { if (task?.status === 'running') startTask(task.taskId); })
      .catch(() => { /* no running scan — fine */ });
  }, [loadCached, projectId, startTask]);

  useEffect(() => {
    if (!scanResult) return;
    setResult(scanResult);
    setScanned(true);
    void loadCached();
  }, [scanResult, loadCached]);

  const toggleRow = useCallback((id: string) => setExpanded((m) => ({ ...m, [id]: !m[id] })), []);

  const confirmDismiss = useCallback(async () => {
    if (!projectId || !modalKey || rationale.trim().length < MIN_RATIONALE) return;
    try {
      await api.substanceProfile.dismiss(projectId, modalKey, rationale.trim());
      setModalKey(null);
      setRationale('');
      await loadCached();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dismiss');
    }
  }, [projectId, modalKey, rationale, loadCached]);

  const restoreFinding = useCallback(async (findingKey: string) => {
    if (!projectId) return;
    try {
      await api.substanceProfile.undismiss(projectId, findingKey);
      await loadCached();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore');
    }
  }, [projectId, loadCached]);

  if (!activeProject) {
    return <div className="p-8 text-sm text-slate-500">Select a project to view the Substance Profile.</div>;
  }

  const substances = result?.substances ?? [];
  const unreadableCount = result?.errors?.length ?? 0;
  const scanIncomplete = result != null && (result.status === 'incomplete' || unreadableCount > 0);
  const rValid = rationale.trim().length >= MIN_RATIONALE;
  const orphans = result?.orphans;

  return (
    <div className="max-w-5xl mx-auto p-6 flex flex-col" style={{ rowGap: '18px' }}>
      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex justify-between items-start gap-5">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-orange">Chrona Review</div>
            <h1 className="text-xl font-bold tracking-tight text-brand-charcoal mt-1">Substance Profile</h1>
            <p className="text-[13px] leading-relaxed text-slate-500 mt-2 max-w-xl">
              One table per substance. Molecular weight triages like the Study Profile — quiet when sources agree,
              drawing the eye on a drift or a calculated-vs-measured mismatch. Strength and composition arrive next.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void runScan()}
            disabled={!canRunScans || scanning || loading}
            title={!canRunScans ? 'Requires Contributor role' : undefined}
            className="flex-none inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${scanning ? 'animate-spin' : ''}`} /> {scanned ? 'Re-scan' : 'Scan'}
          </button>
        </div>

        <div className="flex items-center gap-4 flex-wrap pt-4 border-t border-slate-100" style={{ marginTop: '18px' }}>
          {scanning ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                <LoaderCircle className="w-3 h-3 animate-spin" />
                {taskState.totalDocs > 0
                  ? `Scanning ${taskState.completedDocs}/${taskState.totalDocs}${taskState.currentDocument ? ` — ${taskState.currentDocument}` : ''}`
                  : 'Scanning'}
              </span>
              <button
                type="button"
                onClick={() => void cancelScan()}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
            </>
          ) : taskState.stoppedEarly ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
              <X className="w-3 h-3" /> {taskState.stoppedMessage || 'Scan cancelled'}
            </span>
          ) : scanned && result?.stale ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <Clock className="w-3 h-3" /> Showing last results
            </span>
          ) : scanned ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <Check className="w-3 h-3" /> Scan complete
            </span>
          ) : null}
        </div>
      </section>

      {(error || taskState.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error || taskState.error}</div>
      )}

      {/* out-of-date results (A2F-1281): exact scan cache missed (newer scanner or changed
          document set) — showing the last stored profile rather than blanking. Non-blocking. */}
      {result?.stale && !scanning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-start gap-2">
          <RotateCcw className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Showing your last results — they may be out of date because{' '}
            {result.staleReason === 'doc_set'
              ? 'the document set changed since this scan.'
              : 'the analysis was improved since this scan.'}{' '}
            <button
              type="button"
              onClick={() => void runScan()}
              disabled={!canRunScans}
              className="font-semibold underline underline-offset-2 hover:text-amber-900 disabled:no-underline disabled:opacity-60"
            >
              Re-scan to refresh
            </button>
            {!canRunScans && ' (requires Contributor role).'}
          </span>
        </div>
      )}

      {scanIncomplete && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-start gap-2">
          <EyeOff className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {unreadableCount > 0
              ? `${unreadableCount} document${unreadableCount === 1 ? '' : 's'} could not be read — results are incomplete.`
              : 'Extraction was incomplete — results may be partial.'}
          </span>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <LoaderCircle className="w-6 h-6 mx-auto animate-spin text-brand-orange" />
          <div className="mt-2 text-sm text-slate-500">Loading…</div>
        </div>
      ) : scanning ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center flex flex-col items-center gap-3.5">
          <LoaderCircle className="w-8 h-8 animate-spin text-brand-orange" />
          <div className="text-base font-semibold text-slate-900">Assembling substance profiles…</div>
          <div className="max-w-md text-[13px] leading-relaxed text-slate-500">
            Reading molecular weights and comparing calculated vs measured values across the submission.
          </div>
        </div>
      ) : !scanned ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FlaskConical className="w-7 h-7 mx-auto text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-800">No scan has been run yet</div>
          <div className="mt-1 text-xs text-slate-500">
            {canRunScans ? 'Run a scan to assemble the substance profiles.' : 'Ask a contributor to run a scan.'}
          </div>
          {canRunScans && (
            <button
              type="button"
              onClick={() => void runScan()}
              className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:opacity-90"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Run scan
            </button>
          )}
        </div>
      ) : substances.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FlaskConical className="w-7 h-7 mx-auto text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-800">No substances found</div>
          <div className="mt-1 text-xs text-slate-500">No drug-substance facts were extracted from this submission.</div>
        </div>
      ) : (
        <div className="flex flex-col" style={{ rowGap: '18px' }}>
          {substances.map((entity) => (
            <SubstanceCard
              key={entity.substanceId}
              entity={entity}
              expanded={expanded}
              toggleRow={toggleRow}
              canDismiss={canDismissFindings}
              onDismiss={(key) => { setRationale(''); setModalKey(key); }}
              onRestore={(key) => void restoreFinding(key)}
            />
          ))}

          {orphans && orphans.count > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2">
                <FileQuestion className="w-4 h-4 text-slate-400" />
                <span className="text-[13px] font-semibold text-slate-700">
                  {orphans.count} fact{orphans.count === 1 ? '' : 's'} couldn&apos;t be attributed to a substance
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(orphans.byFactType).map(([ft, items]) => (
                  <span key={ft} className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-0.5">
                    {ft} ×{items.length}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] leading-relaxed text-slate-400 border-t border-slate-100 pt-3">
        Strength / fill mass and composition are coming — they need a deterministic grouping key (A2F-1374) and
        structured composition extraction (A2F-1372) before they can be surfaced without over-firing.
      </div>

      {result && (
        <ScanFooter
          data={{
            scannedAt: result.scannedAt,
            modelUsed: 'Substance reconciliation',
            totalFiles: result.totalFiles,
            errors: (result.errors ?? []).map((e, i) => ({
              document: String((e as Record<string, unknown>).document ?? '(unknown)'),
              message: String((e as Record<string, unknown>).message ?? ''),
              index: i,
            })),
          }}
        />
      )}

      {modalKey && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-6 z-[100]">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Substance Profile · finding</div>
                <h2 className="text-lg font-bold leading-tight text-slate-900 mt-1">Dismiss finding</h2>
              </div>
              <button type="button" onClick={() => setModalKey(null)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[13px] leading-relaxed text-slate-600 mt-3.5">
              You are dismissing this finding. This is an audited decision — a written rationale is required and is
              retained in the submission&apos;s audit trail.
            </p>
            <div className="mt-3.5">
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Reason for dismissal (required, minimum {MIN_RATIONALE} characters)
              </label>
              <textarea
                rows={3}
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="e.g. The calculated–measured gap is the known HCl salt-form delta, confirmed with analytical."
                className="w-full border border-slate-200 rounded-lg p-3 text-[13px] leading-relaxed text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 resize-y"
                autoFocus
              />
              <div className="text-[11px] font-mono text-slate-400 mt-1.5">{rationale.trim().length} / {MIN_RATIONALE} characters minimum</div>
            </div>
            <div className="flex items-center gap-2 mt-3 px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-lg">
              <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[11px] leading-snug text-slate-500">Recorded as an attributable, time-stamped decision · 21 CFR Part 11</span>
            </div>
            <div className="flex gap-2.5 justify-end mt-4">
              <button
                type="button"
                onClick={() => setModalKey(null)}
                className="px-3.5 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDismiss()}
                disabled={!rValid}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Shield className="w-3.5 h-3.5" /> Dismiss with rationale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
